<script lang="ts">
	import { onMount } from 'svelte';
	import { writable } from 'svelte/store';
	import Button from './Button.svelte';
	import Icon from './Icon.svelte';
	import SocialTwitter from '@icons/SocialTwitter.svelte';
	import SocialLinkedIn from '@icons/SocialLinkedIn.svelte';
	import SocialFacebook from '@icons/SocialFacebook.svelte';
	import SocialWhatsApp from '@icons/SocialWhatsApp.svelte';
	import ActionLink from '@icons/ActionLink.svelte';
	import ActionForward from '@icons/ActionForward.svelte';
	import ActionClose from '@icons/ActionClose.svelte';
	import ActionCheckmarkCircle from '@icons/ActionCheckmarkCircle.svelte';

	export let title = '';
	export let customClass = '';

	const isWebShareAPISupported = writable(false);
	const isSubmenuOpen = writable(false);
	const isCopied = writable(false);

	let wrapperRef: HTMLDivElement | null;
	let shareButtonRef: HTMLButtonElement | null;
	let menuPosition: 'top' | 'bottom' = 'bottom';

	onMount(() => {
		isWebShareAPISupported.set(!!navigator.share);

		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node;

			if (
				wrapperRef &&
				!wrapperRef.contains(target) &&
				shareButtonRef &&
				!shareButtonRef.contains(target)
			) {
				isSubmenuOpen.set(false);
			}
		};

		const handleEscapeKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				isSubmenuOpen.set(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleEscapeKey);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleEscapeKey);
		};
	});

	async function shareUsingWebShareAPI() {
		if (navigator.share) {
			try {
				await navigator.share({
					title,
					url: window.location.href
				});
			} catch (error) {
				if (error instanceof DOMException && error.name === 'AbortError') {
					return;
				}

				console.log(
					`Using Fallback Sharing due to Error sharing via Web Share API: ${error}`,
					JSON.stringify(error)
				);
				isSubmenuOpen.update((n) => !n);
				isCopied.set(false);
			}
		}
	}

	async function share() {
		menuPosition = getOptimalPosition();

		const supported = $isWebShareAPISupported;
		if (supported) {
			await shareUsingWebShareAPI();
		} else {
			isSubmenuOpen.update((n) => !n);
			isCopied.set(false);
		}
	}

	function copyToClipboard() {
		navigator?.clipboard?.writeText(window.location.href);
		isCopied.set(true);
	}

	function getOptimalPosition(): 'top' | 'bottom' {
		const rect = shareButtonRef?.getBoundingClientRect();
		if (!rect) return 'bottom';
		const spaceBelow = window.innerHeight - rect.bottom;

		if (spaceBelow < 290) {
			return 'top';
		} else {
			return 'bottom';
		}
	}
</script>

<div class="relative {customClass}">
	<button
		class="flex gap-3 items-center focus:underline hover:underline"
		on:click={share}
		bind:this={shareButtonRef}
	>
		<span class="w-7.5 h-7.5 rounded-full border border-gray-1 flex justify-center items-center">
			<Icon component={ActionForward} />
		</span>

		Teilen
	</button>

	{#if $isSubmenuOpen}
		<div
			bind:this={wrapperRef}
			class="shadow-lpg-tile w-74 z-[10] absolute mt-3 p-3 pt-6 bg-white rounded md:translate-x-0 md:left-0 lg:left-auto lg:right-0"
			class:menu-top={menuPosition === 'top'}
			class:menu-bottom={menuPosition === 'bottom'}
		>
			<button class="absolute top-1.5 right-1.5 z-[1]" on:click={() => isSubmenuOpen.set(false)}>
				<Icon component={ActionClose} class="!w-4.5 !h-4.5" />
				<span class="sr-only">Schließen</span>
			</button>
			<div class="flex flex-col gap-3 p-4 relative items-stretch content-center">
				<Button variant="secondary" on:click={copyToClipboard}>
					{#if !$isCopied}
						<Icon component={ActionLink} class="mr-1.5" />
						Link kopieren
					{:else}
						<Icon class="text-function-green mr-1.5" component={ActionCheckmarkCircle} />
						<span class="text-function-green">Link kopiert!</span>
					{/if}
				</Button>

				<Button
					variant="secondary"
					tag="a"
					target="_blank"
					href={`https://api.whatsapp.com/send?text=${encodeURIComponent(window.location.href)}`}
				>
					<Icon component={SocialWhatsApp} class="mr-1.5" />
					WhatsApp
				</Button>
				<Button
					variant="secondary"
					tag="a"
					target="_blank"
					href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
						window.location.href
					)}`}
				>
					<Icon component={SocialFacebook} class="mr-1.5" />
					Facebook
				</Button>

				<Button
					variant="secondary"
					tag="a"
					target="_blank"
					href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
						window.location.href
					)}&text=${encodeURIComponent(title)}`}
				>
					<Icon component={SocialTwitter} class="mr-1.5" />
					Twitter
				</Button>

				<Button
					variant="secondary"
					tag="a"
					target="_blank"
					href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
						window.location.href
					)}`}
				>
					<Icon component={SocialLinkedIn} class="mr-1.5" />
					LinkedIn
				</Button>
			</div>
		</div>
	{/if}
</div>

<style lang="postcss">
	.menu-top {
		@apply bottom-0 mb-9;
	}

	.menu-bottom {
		@apply top-0 mt-9;
	}
</style>
